import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip } from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { selectCompanyListState } from "../../state/companyList/companyListSelector";
import { selectLinkedinPromotionTotalStats } from "../../state/linkedinPromotion/linkedinPromotionSelector";
import { getCompanyList } from "../../state/companyList/companyListSlice";
import TableLoaderSkeleton from "../../global/Loader/TableLoaderSkeleton";
import GlobalEmptyPage from "../../common/GlobalEmptyPage/GlobalEmptyPage";
import { COMPANY_STATUS_COLOR, COMPANY_STATUS_TEXT, USER_STATUS } from "../../helpers/constant/coreConstant";
import CompanyMenuAction from "./CompanyMenuAction";
import GlobalTablePagination from "../../global/Pagination/GlobalTablePagination";
import { getFullName } from "../../helpers/utils/getFullName";
import CompaniesHeader from "./CompaniesHeader";

const headCells = [
  {
    id: "Company Info",
    label: "Company Info",
  },
  {
    id: "Super Admin",
    label: "Super Admin",
  },
  {
    id: "Active Jobs",
    label: "Active Jobs",
  },
  {
    id: "Total Jobs",
    label: "Total Jobs",
  },
  {
    id: "Joining Date",
    label: "Joining Date",
  },
  {
    id: "Status",
    label: "Status",
  },
  {
    id: "Actions",
    label: "Actions",
  },
];

const TableHeader = () => {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="left" padding={"normal"}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const Companies = () => {
  const dispatch = useDispatch();
  const { data: companyList, isLoading, page, perPage, count, filters } = useSelector(selectCompanyListState);
  const { activeDeals = [] } = useSelector(selectLinkedinPromotionTotalStats);

  useEffect(() => {
    dispatch(getCompanyList({ page: 1, perPage: perPage, ...filters }));
  }, [dispatch, perPage, filters]);

  const onChangePage = (newPage) => {
    dispatch(getCompanyList({ page: newPage, perPage: perPage, ...filters }));
  };

  const onChangePerPage = (perPage, newPage) => {
    dispatch(getCompanyList({ page: newPage, perPage: perPage, ...filters }));
  };

  let content = null;
  if (isLoading) {
    content = <TableLoaderSkeleton columns={7} numberOfRows={10} />;
  } else if (companyList?.length === 0) {
    content = <GlobalEmptyPage title={"No Company found!"} />;
  } else if (companyList?.length > 0) {
    content = (
      <div>
        <TableContainer sx={{ maxHeight: `calc(100vh - 300px)` }}>
          <Table sx={{ minWidth: 750 }}>
            <TableHeader />
            <TableBody>
              {companyList.map((companyInfo) => {
                const { status, ownerInfo, createdAt } = companyInfo || {};
                const { email, phone } = ownerInfo || {};
                const ownerName = getFullName(ownerInfo?.firstName, ownerInfo?.lastName) || "-";
                const joiningDate = format(new Date(createdAt), "dd MMM, yyyy");

                const hasLinkedinDeal = activeDeals?.find((deal) => deal.companyId === companyInfo.id);

                return (
                  <TableRow key={companyInfo.id} hover>
                    <TableCell align="left">
                      <Stack spacing={0.5} sx={{ minWidth: 220, maxWidth: 320 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant={"body1"} fontWeight={700} title={companyInfo.name} className={"text-truncate"}>
                            {companyInfo?.name || "-"}
                          </Typography>
                          {hasLinkedinDeal && (
                            <Tooltip
                              title={hasLinkedinDeal.integrationType === "MANAGED_BY_COMPANY" ? "LinkedIn Talent Solutions - Managed by Company" : "LinkedIn Talent Solutions - Managed by ATS"}
                              arrow
                            >
                              <LinkedInIcon color={hasLinkedinDeal.integrationType === "MANAGED_BY_COMPANY" ? "secondary" : "primary"} sx={{ fontSize: 20 }} />
                            </Tooltip>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          ID: {companyInfo?.id || "-"}
                        </Typography>
                        {companyInfo?.linkedInLink ? (
                          <Typography
                            component="a"
                            variant="caption"
                            href={companyInfo.linkedInLink}
                            target="_blank"
                            rel="noreferrer"
                            title={companyInfo.linkedInLink}
                            className={"text-truncate"}
                            sx={{ color: "primary.main", display: "block" }}
                          >
                            {companyInfo.linkedInLink}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No LinkedIn
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="left" title={ownerName}>
                      <Stack spacing={0.75} sx={{ minWidth: 220 }}>
                        <Box>
                          <Typography variant="body2">{email || "-"}</Typography>
                          {ownerInfo?.status === USER_STATUS.ACTIVE ? (
                            <Typography variant="caption" color="success.main">
                              <strong>[Verified]</strong>
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="secondary">
                              <strong>[Not Verified]</strong>
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {phone || "No phone"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="left">{companyInfo?.totalActiveJobPost || 0}</TableCell>
                    <TableCell align="left">{companyInfo?.totalJobPost || 0}</TableCell>
                    <TableCell align="left">{joiningDate}</TableCell>
                    <TableCell align="left">
                      <Chip size="small" label={COMPANY_STATUS_TEXT[status || 0]} color={COMPANY_STATUS_COLOR[status]} />
                    </TableCell>
                    <TableCell align="left">
                      <CompanyMenuAction companyInfo={companyInfo} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <GlobalTablePagination count={count} page={page} rowsPerPage={perPage} onChangePerPage={onChangePerPage} onChangePage={onChangePage} />
      </div>
    );
  }

  return (
    <>
      <Paper elevation={0}>
        <CompaniesHeader />
        {content}
      </Paper>
    </>
  );
};
export default Companies;
